import './GridColumnSlider.css'

import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { SliderArrow16 } from '@makyoapp/makyo-icon/icon'
import { TailwindColors, useTheme } from '@makyoapp/makyo-ui'

const getPercentage = (containerWidth: number, distanceMoved: number) => {
  return (distanceMoved / containerWidth) * 100
}

const limitNumberWithinRange = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max)
}

const nearestN = (N: number, number: number) => Math.ceil(number / N) * N

export interface GridColumnSectionProps {
  name: string
  color: string
  width: number
  onSliderSelect: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}
export function GridColumnSection({ name, color, width, onSliderSelect }: GridColumnSectionProps) {
  return (
    <div className='gcs-section' style={{ background: color, width: `${width}%` }}>
      <div className='name'>{name}</div>
      <div>{`${width?.toFixed(0)}%`}</div>
      <div className='slider-button' onMouseDown={onSliderSelect}>
        <SliderArrow16 />
      </div>
    </div>
  )
}

export function GridColumnSlider({
  id,
  count,
  value,
  onChange,
}: {
  id: string
  count: number
  value: number[]
  onChange: (next: number[]) => void
}) {
  const isDark = useTheme().state.mode === 'dark'
  const clr = ['200', '300', '400']
  const clrDark = ['500', '600', '700']
  const sliderRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  return (
    <div
      id={id}
      className='gcs-slider'
      ref={sliderRef}
      style={{
        width: '100%',
        display: 'flex',
      }}
    >
      {Array.from(Array(count)).map((_, index) => (
        <GridColumnSection
          width={value[index]}
          key={index}
          name={t('Column {{index}}', { index: index + 1 })}
          color={!isDark ? TailwindColors.zinc[clr[index] as never] : TailwindColors.slate[clrDark[index] as never]}
          onSliderSelect={(e) => {
            e.preventDefault()
            document.body.style.cursor = 'ew-resize'

            const startDragX = e.pageX
            const sliderWidth = sliderRef.current?.offsetWidth || 0

            const resize = (e: MouseEvent & TouchEvent) => {
              e.preventDefault()
              const endDragX = e.touches ? e.touches[0].pageX : e.pageX
              const distanceMoved = endDragX - startDragX
              const maxPercent = value.reduce((p, w, i) => {
                if (i !== index && i !== index + 1) {
                  return p - w
                } else {
                  return p
                }
              }, 100) //widths[index] + widths[index + 1]

              const percentageMoved = nearestN(1, getPercentage(sliderWidth, distanceMoved))

              const _widths = [...value]

              const prevPercentage = _widths[index]

              const newPercentage = prevPercentage + percentageMoved
              const currentSectionWidth = limitNumberWithinRange(newPercentage, 10, maxPercent - 10)
              _widths[index] = currentSectionWidth

              const nextSectionIndex = index + 1

              const nextSectionNewPercentage = _widths[nextSectionIndex] - percentageMoved
              const nextSectionWidth = limitNumberWithinRange(nextSectionNewPercentage, 10, maxPercent - 10)
              /*_widths[nextSectionIndex] =
                nextSectionWidth + currentSectionWidth > maxPercent
                  ? maxPercent - currentSectionWidth
                  : nextSectionWidth*/
              _widths[nextSectionIndex] = nextSectionWidth

              if (count > 2) {
                if (_widths[index] === 0) {
                  _widths[nextSectionIndex] = maxPercent
                  _widths.splice(index, 1)
                  //setTags(tags.filter((t, i) => i !== index))
                  removeEventListener()
                }
                if (_widths[nextSectionIndex] === 0) {
                  _widths[index] = maxPercent
                  _widths.splice(nextSectionIndex, 1)
                  //setTags(tags.filter((t, i) => i !== nextSectionIndex))
                  removeEventListener()
                }
              }

              onChange(_widths)
            }

            window.addEventListener('pointermove', resize as EventListener)
            window.addEventListener('touchmove', resize as EventListener)

            const removeEventListener = () => {
              window.removeEventListener('pointermove', resize as EventListener)
              window.removeEventListener('touchmove', resize as EventListener)
            }

            const handleEventUp = (e: Event) => {
              e.preventDefault()
              document.body.style.cursor = 'initial'
              removeEventListener()
            }

            window.addEventListener('touchend', handleEventUp)
            window.addEventListener('pointerup', handleEventUp)
          }}
        />
      ))}
    </div>
  )
}
